import {
  useUserProfile,
  WbSidebarSubmenuItemProps,
} from '@agilelab/plugin-wb-platform';
import { makeStyles, Typography } from '@material-ui/core';
import { WbTooltip } from '../../WbTooltip';
import clsx from 'clsx';
import { useContext } from 'react';
import {
  SidebarConfig,
  WbSidebarConfigContext,
  WbSidebarItemWithSubmenuContext,
} from '../config';
import { useWbSidebarOpenState } from '../WbSidebarOpenStateContext';
import { useWbSidebarMobileState } from '../WbNavbarMobileStateContext';
import { WbLink } from '../../WbLink';
import { WbUserAvatar } from '../../WbAvatar';

const useStyle = makeStyles(theme => ({
  root: (props: SidebarConfig) => ({
    display: 'flex',
    alignItems: 'center',
    height: '64px',
    backgroundColor: theme.palette.grey[50],
    paddingInline: props.userBadgeSubMenuPadding ?? props.userBadgePadding,
    paddingBlock: '8px',
    gap: '8px',
  }),
  drawerWidth: (props: SidebarConfig) => ({
    width: props.drawerWidthClosed,
  }),
  drawerOpen: () => ({
    width: '100%',
  }),
  // keep this as a function to override on mobile drawerOpen
  mobile: () => ({
    width: '100%',
    height: '50px',
  }),
  avatar: (props: SidebarConfig) => ({
    /**
     * TODO We target the div with the avatar class specifically to increase specificity
     * because otherwise in production builds mui class overrides this class for some reasons, why this happens?
     */
    'div&': {
      width: props.userBadgeDiameter,
      height: props.userBadgeDiameter,
    },
  }),
  displayName: {
    color: theme.palette.black,
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': 2,
    overflow: 'hidden',
    display: '-webkit-box',
    wordBreak: 'break-all',
  },
}));

type WbSidebarAvatarProps = Omit<WbSidebarSubmenuItemProps, 'title'> & {
  to: string;
};

export const WbSidebarSubmenuAvatar = ({ to }: WbSidebarAvatarProps) => {
  const { isOpen } = useWbSidebarOpenState();
  const { isMobile, setMenuOpen } = useWbSidebarMobileState();

  const { sidebarConfig } = useContext(WbSidebarConfigContext);
  const { isSubmenuOpen } = useContext(WbSidebarItemWithSubmenuContext);

  const { displayName } = useUserProfile();

  const classes = useStyle(sidebarConfig);

  const handleClick = isMobile
    ? () => {
        setMenuOpen(false);
      }
    : undefined;

  return (
    <WbLink
      className={clsx(
        classes.root,
        classes.drawerWidth,
        isOpen && classes.drawerOpen,
        isMobile && classes.mobile,
      )}
      onClick={handleClick}
      underline="none"
      aria-label="Profile Settings"
      to={to}
      tabIndex={isSubmenuOpen ? 0 : -1}
    >
      <WbUserAvatar classes={{ avatar: classes.avatar }} singleInitial />
      {isOpen && (
        <WbTooltip title={displayName} placement="right">
          <Typography
            className={classes.displayName}
            variant="body1"
            component="span"
          >
            {displayName}
          </Typography>
        </WbTooltip>
      )}
    </WbLink>
  );
};
